import { getAuthFromAdminPayloadFromUrl, cookieManager } from './chunk-2RR4A6N3.js';
import { config } from './chunk-Q5EN5ZLW.js';

// ../base/api/error.ts
var JwApiError = class extends Error {
  name = "JwApiError";
  response;
  parsedMessage;
  constructor(response) {
    let parsedMessage = "An unknown error has occurred.";
    let status;
    if (response && response.data && typeof response.data === "object" && "errors" in response.data && Array.isArray(response.data.errors)) {
      parsedMessage = response.data.errors.map((e) => e.description).join("\n");
      status = response.status;
    }
    super((status ? `[${status}] ` : "") + parsedMessage);
    this.response = response;
    this.parsedMessage = parsedMessage;
  }
  toString() {
    return this.parsedMessage;
  }
};
function isJwApiError(e) {
  return e instanceof JwApiError;
}

// ../base/auth/error.ts
var ERROR_CODES = ["MISSING_TOKEN", "MFA_REQUIRED", "RESPONSE_EMPTY"];
var JwAuthError = class extends Error {
  static CODES = ERROR_CODES;
  name = "JwAuthError";
  code;
  constructor(code) {
    super(code);
    this.code = code;
  }
  toString() {
    return this.code;
  }
};
function isJwAuthError(e) {
  return e instanceof JwAuthError;
}

// ../../node_modules/@jwplayer/fe-common/module/util/promise/index.js
var STRIP = "AbortError: ";
var AbortError = class extends Error {
  name = "AbortError";
  constructor(signal) {
    let reason;
    if (signal instanceof AbortSignal) {
      reason = String(signal.reason);
      reason = reason.startsWith(STRIP) ? reason.slice(STRIP.length) : reason;
    }
    super(reason);
  }
};
var PromiseUtil = {
  /**
   * Returns a cached promise while it is pending
   *
   * Once the promise is resolved, the cache is cleared and subsequent calls will create a new promise.
   */
  coalesce(fn) {
    let promise;
    return (...args) => {
      if (promise) {
        return promise;
      }
      promise = fn(...args).finally(() => {
        promise = void 0;
      });
      return promise;
    };
  },
  /**
   * Returns a cached promise, ensuring the promise runs only once
   *
   * Unlike memoization, this returns the promise, not the result.
   */
  once(fn) {
    let promise = void 0;
    return (...args) => {
      if (promise) {
        return promise;
      }
      promise = fn(...args);
      return promise;
    };
  },
  /**
   * Extract a Promise's resolve and reject functions so that they can be fired externally.
   */
  extract() {
    let resolve;
    let reject;
    const promise = new Promise((res, rej) => {
      resolve = res;
      reject = rej;
    });
    return {
      promise,
      resolve: (res) => resolve(res),
      reject: (err) => reject(err)
    };
  },
  /**
   * Only return latest result.
   *
   * All promises that are created earlier but resolved later will throw an AbortError.
   */
  latest(fn) {
    let abortControllers = [];
    return async (...args) => {
      abortControllers.forEach((ctrl2) => {
        ctrl2.abort();
      });
      const ctrl = new AbortController();
      abortControllers.push(ctrl);
      try {
        const ret = await fn(...args);
        if (ctrl.signal.aborted) {
          throw new AbortError(ctrl.signal);
        }
        return ret;
      } finally {
        abortControllers = abortControllers.filter((c) => c !== ctrl);
      }
    };
  }
};

// ../base/auth/manager.ts
var AuthManager = class {
  eventHandlers = {
    authenticated: [],
    refreshFailed: [],
    expired: [],
    cleared: []
  };
  /**
   * Cached 'session' object
   */
  cache;
  lastRefresh;
  tokenRefreshing = false;
  opts;
  /**
   * Cached token
   */
  cachedToken;
  constructor(opts) {
    this.opts = {
      ...opts
    };
    document.addEventListener("visibilitychange", () => {
      if (!document.hidden) {
        this.hasTokenExpired();
      }
    });
  }
  /**
   * Clear the cached session and auth token (cookie).
   */
  clear() {
    this.cachedToken = void 0;
    cookieManager.deleteCookie(this.opts.authCookieName());
    this.cache = void 0;
    this.emit("cleared");
  }
  /**
   * Emit event (with arguments)
   */
  emit(event, ...args) {
    this.eventHandlers[event].forEach((h) => h.apply(h, args));
  }
  /**
   * API call needs to be implemented in child class
   */
  async authenticateApi(_opts) {
    throw new Error("Not implemented");
  }
  /**
   * API call needs to be implemented in child class
   */
  async loginApi(_params, _opts) {
    throw new Error("Not implemented");
  }
  /**
   * Returns an existing session if there is one. If not, then an API
   * call is made to refresh the session from an auth cookie.
   */
  async authenticate(opts) {
    const cache = this.getCache();
    if (cache) {
      return cache;
    }
    if (!(this.getToken() || opts?.authToken)) {
      throw new JwAuthError("MISSING_TOKEN");
    }
    try {
      const res = await this.authenticateApi(opts);
      this.setCache(res.obj, res.token);
      return res.obj;
    } catch (err) {
      if (isJwApiError(err) && String(err.response?.status).startsWith("4")) {
        this.clear();
      }
      throw err;
    }
  }
  async login(params, opts) {
    const res = await this.loginApi(params, opts);
    this.setCache(res.obj, res.token);
    return res.obj;
  }
  logout() {
    this.clear();
  }
  hasTokenExpired() {
    if (typeof this.lastRefresh === "number") {
      const tokenAge = Date.now() - this.lastRefresh;
      const { expired: maxTokenAge, stale: staleTokenAge } = this.opts.authTokenTtl();
      if (tokenAge > maxTokenAge) {
        this.clear();
        this.emit("expired");
        return true;
      }
      if (tokenAge > staleTokenAge && !this.tokenRefreshing) {
        this.tokenRefreshing = true;
        this.authenticateApi().then((res) => {
          this.setCache(res.obj, res.token);
        }).catch(() => {
        }).finally(() => {
          this.tokenRefreshing = false;
        });
      }
    }
    return false;
  }
  getToken() {
    if (this.hasTokenExpired()) {
      return;
    }
    if (!this.cachedToken) {
      this.cachedToken = cookieManager.getCookie(this.opts.authCookieName()) || void 0;
    }
    return this.cachedToken;
  }
  getCache() {
    if (this.hasTokenExpired()) {
      return;
    }
    if (!this.cache) {
      return;
    }
    return this.cache;
  }
  /**
   * Set cached session object and auth token
   */
  setCache(cache, token) {
    this.lastRefresh = Date.now();
    this.cache = cache;
    this.cachedToken = token;
    cookieManager.setCookie(this.opts.authCookieName(), token);
    this.emit("authenticated", cache);
  }
  /**
   * Only used in testing context and for sharing session from Companion v1.
   */
  __INTERNAL_SET_CACHE__ = (cache, token) => {
    this.cache = structuredClone(cache);
    this.cachedToken = token;
  };
  /**
   * Subscribe to event
   *
   * Returns function to unsubscribe
   *
   * @param event Event name
   * @param handler Event handler
   */
  subscribe(event, handler) {
    if (!this.eventHandlers[event].includes(handler)) {
      this.eventHandlers[event].push(handler);
    }
    return () => {
      this.eventHandlers[event] = this.eventHandlers[event].filter(
        (h) => h !== handler
      );
    };
  }
};

// src/auth/util.ts
function extractAuthToken(obj) {
  return obj.authentication.dashboard;
}

// src/auth/index.ts
var AuthGatewayManager = class extends AuthManager {
  constructor() {
    super({
      authCookieName: config.select((c) => c.auth.cookies.token),
      authTokenTtl: config.select((c) => c.auth.tokenTtl)
    });
  }
  async authenticate(opts) {
    const { authToken, ...fromAdminPayload } = getAuthFromAdminPayloadFromUrl(config.get().auth.fromAdminQueryParam) ?? {};
    if (authToken) {
      this.clear();
      cookieManager.setCookie(config.select((c) => c.auth.cookies.authFromAdmin)(), JSON.stringify(fromAdminPayload));
    }
    return super.authenticate({ ...opts, authToken });
  }
  clear() {
    cookieManager.deleteCookie(config.select((c) => c.auth.cookies.authFromAdmin)());
    super.clear();
  }
  authenticateApi = PromiseUtil.coalesce(async (opts) => {
    const { jwApi } = await import('./api-EJIS42C7.js');
    const obj = (await jwApi("dashboard-scope", "read", {}, opts)).data;
    return {
      obj,
      token: extractAuthToken(obj)
    };
  });
  /**
   * Authenticate a user with the API Gateway.
   *
   * In case MFA is set up for the user, the error `MFA_REQUIRED` is thrown when
   * the user needs to enter their MFA code.
   */
  loginApi = PromiseUtil.coalesce(async ({ email, password, mfaCode, rememberMfa }, opts) => {
    const { jwApi } = await import('./api-EJIS42C7.js');
    email = email.trim();
    password = password.trim();
    mfaCode = mfaCode?.trim();
    const mfaCookieIdentifier = config.get().auth.cookies.mfa.identifier;
    const mfaSignature = cookieManager.getCookie(mfaCookieIdentifier)?.trim();
    const res = await jwApi(
      "dashboard-scope",
      "create",
      {
        input: {
          email,
          password,
          // ⚠️ Don't pass empty strings
          code: mfaCode || void 0,
          remember_mfa: mfaCode ? !!rememberMfa : void 0,
          mfa_signature: mfaSignature || void 0
        }
      },
      opts
    );
    if (res.status === 204) {
      cookieManager.deleteCookie(mfaCookieIdentifier);
      throw new JwAuthError("MFA_REQUIRED");
    }
    if (!res.data) {
      throw new JwAuthError("RESPONSE_EMPTY");
    }
    if (res.data.authentication.mfa && typeof res.data.authentication.mfa === "string") {
      cookieManager.setCookie(mfaCookieIdentifier, res.data.authentication.mfa, {
        "Max-Age": config.get().auth.cookies.mfa.ttl
      });
    }
    cookieManager.deleteCookie(config.select((c) => c.auth.cookies.authFromAdmin)());
    return {
      obj: res.data,
      token: extractAuthToken(res.data)
    };
  });
};
var auth = new AuthGatewayManager();

export { JwApiError, JwAuthError, auth, isJwApiError, isJwAuthError };
//# sourceMappingURL=chunk-6ZDOB32G.js.map
//# sourceMappingURL=chunk-6ZDOB32G.js.map